"use client"

import { useState, useMemo } from "react"
import HomeDetailsWrapperAccordion from "../home-details-wrapper-accordion"
import CompletionStatusDisplay from "../completion-status-display"
import GoodwillHomeStatusDisplay from "../goodwill-home-status-display"
import ReliefHomeStatusDisplay from "../relief-home-status-display"
import RebuildingHomeStatusDisplay from "../rebuilding-home-status-display"
import FormDateStampedExpandableTextareaInput from "../../../../shared/components/form-inputs/form-date-stamped-expandable-textarea-input"
import ProjectGeneralNotesWithMentions from "./project-general-notes-with-mentions"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useReadOnly } from "../../context/read-only-context"
import ProjectGeneralNotesVolunteer from "./project-general-notes-volunteer"
import { Input } from "@/shared/components/ui/input"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/shared/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Search, Info } from "lucide-react"

function HomeOverallStatusForm() {
    const { control } = useFormContext<HomeWithHomeOwnerFormType>()
    const { isReadOnly, canEditProjectNotes } = useReadOnly()
    const projectGeneralNotes = useWatch({ control, name: "home.projectGeneralNotes", defaultValue: "" })
    const [pgnFilterKeyword, setPgnFilterKeyword] = useState("")
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const [pgnInfoPopoverOpen, setPgnInfoPopoverOpen] = useState(false)

    const notesLines = useMemo(() => {
        const text = projectGeneralNotes ?? ""
        return text.split("\n").filter((line) => line.trim().length > 0)
    }, [projectGeneralNotes])

    const filteredLines = useMemo(() => {
        if (!pgnFilterKeyword.trim()) return notesLines
        const kw = pgnFilterKeyword.trim().toLowerCase()
        return notesLines.filter((line) => line.toLowerCase().includes(kw))
    }, [notesLines, pgnFilterKeyword])

    const isFilterActive = pgnFilterKeyword.trim().length > 0

    const PGN_HEADING = 'Type @ to tag a notification'
    const PGN_INFO = 'Use this field to add dated notes. Press Enter to start a new line and a timestamp with your name will be added automatically. Typing @ tags a volunteer for a notification.'

    const projectGeneralNotesLabelAction = (
        <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">
                Type @ to tag a notification
            </span>
            <Popover open={pgnInfoPopoverOpen} onOpenChange={setPgnInfoPopoverOpen}>
                <TooltipProvider delayDuration={300}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                                    aria-label="Project General Notes info"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                            <p className="font-medium mb-1">{PGN_HEADING}</p>
                            <p>{PGN_INFO}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <PopoverContent side="bottom" className="max-w-[280px] text-sm">
                    <p className="font-medium mb-1">{PGN_HEADING}</p>
                    <p>{PGN_INFO}</p>
                </PopoverContent>
            </Popover>
            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="default"
                                        size="sm"
                                        className="shrink-0 bg-primary hover:bg-primary/90 text-white font-bold h-6 w-8 rounded-full p-0"
                                        aria-label="Filter notes by keyword"
                                    >
                                        <Search className="h-4 w-4" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-h-[90vh] max-w-lg">
                                    <DialogTitle>Filter notes by keyword</DialogTitle>
                                    <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
                                        <div className="space-y-2">
                                            <Label htmlFor="pgn-filter-popup-vol" className="text-sm">Keyword</Label>
                                            <Input id="pgn-filter-popup-vol" type="text" placeholder="Type to filter notes..." value={pgnFilterKeyword} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPgnFilterKeyword(e.target.value)} autoFocus />
                                        </div>
                                        {isFilterActive && (
                                            <div className="space-y-2">
                                                <p className="text-sm text-muted-foreground">
                                                    {filteredLines.length === 0 ? <>No notes match &quot;{pgnFilterKeyword.trim()}&quot;</> : <>Showing {filteredLines.length} of {notesLines.length} note{notesLines.length !== 1 ? "s" : ""} matching &quot;{pgnFilterKeyword.trim()}&quot;</>}
                                                </p>
                                                {filteredLines.length > 0 && (
                                                    <div className="rounded-md border border-input bg-muted/30 p-3 max-h-64 overflow-y-auto">
                                                        <pre className="whitespace-pre-wrap text-sm font-sans">{filteredLines.join("\n")}</pre>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 pt-2">
                                            {isFilterActive && <Button type="button" variant="ghost" size="sm" onClick={() => setPgnFilterKeyword("")}>Clear filter</Button>}
                                            <Button type="button" variant="default" size="sm" onClick={() => setFilterDialogOpen(false)} className="ml-auto">Done</Button>
                                        </div>
                                    </div>
                                </DialogContent>
            </Dialog>
        </div>
    )

    return (
        <HomeDetailsWrapperAccordion 
            heading="Overall Status" 
            wrapperClassName="xl:grid-cols-2"
        >
            <div className="xl:col-span-2">
                <CompletionStatusDisplay className="mb-0" />
            </div>
            <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <ReliefHomeStatusDisplay />
                <RebuildingHomeStatusDisplay />
                <GoodwillHomeStatusDisplay />
            </div>
            <div className="xl:col-span-2 space-y-4">
                {/* Project General Notes: info tooltip + filter on same line as title via labelAction */}
                {isReadOnly && canEditProjectNotes ? (
                    <ProjectGeneralNotesVolunteer
                        name="home.projectGeneralNotes"
                        inputGroupProps={{ 
                            label: "Project General Notes", 
                            wrapperClassName: "xl:col-span-2" 
                        }}
                        defaultRows={1}
                        labelAction={projectGeneralNotesLabelAction}
                    />
                ) : (
                    <ProjectGeneralNotesWithMentions
                        name="home.projectGeneralNotes"
                        control={control}
                        defaultRows={6}
                        inputGroupProps={{ 
                            label: "Project General Notes", 
                            wrapperClassName: "xl:col-span-2" 
                        }}
                        labelAction={projectGeneralNotesLabelAction}
                    />
                )}
            </div>
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeOverallStatusForm
